import React from 'react'
import { Link } from 'react-router-dom'
import { useSEO } from '../hooks/useSEO'
import { ContactForm } from '../components/ContactForm'
import { useCMSSide } from '../hooks/useCMS'

export function UddannelsePage() {
  const { side: cmsSide } = useCMSSide('uddannelse')

  useSEO({
    title: cmsSide?.seo_titel || 'Dokumenter | Trekantens Kropsterapi',
    description: cmsSide?.seo_beskrivelse || 'Dokumenter, RAB-certificering og uddannelsesbeviser for Totum Kropsterapeut og Hypnoterapi Practitioner Bo Steendahl.',
    ogImage: cmsSide?.dele_billede?.[0] || 'https://assets.auxodev.dk/assets/2a253654-8b0d-44c3-813e-14453c64ff7c/1711543962553-15f9fabe-7704-4ead-8e4a-b185986203c1-1.jpg',
  })

  const obligations = [
    '1. At holde sig opdateret med almen sundhedsviden til gavn for sine klienter og deres sundhedsfremme.',
    '2. At vedligeholde og opdatere sin faglige viden inden for sit behandlingsområde.',
    '3. At bidrage sine klienter med professionel vejledning til gavn for deres sundhed og sundhedsfremme.',
    '4. At udøve professionel høj standard og kvalitet i sine behandlinger.',
    '5. At overholde tavshedspligt om oplysninger der erhverves i sin egenskab som behandler.',
    '6. At henvise klienter til andre behandlere eller fagområder, hvis behandlingen ligger uden for behandlerens kompetenceområde.',
    '7. At føre journal og behandle disse efter datalovens foreskrifter.',
    '8. At indhente viden om og overholde gældende lovgivning indenfor sit behandlerområde.',
    '9. At overholde Dansk Behandlerforbunds etiske regler.',
    '10. At bidrage aktivt med viden, samarbejde og sparring til forbundet og dens medlemmer.',
  ]

  return (
    <div className="bg-[#FAF8F5]">
      {/* Hero Section */}
      <section className="relative py-16 lg:py-24 bg-gradient-to-b from-[#F5EFE9] to-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#1b3a4b]/8 text-[#1b3a4b] text-xs font-semibold uppercase tracking-wider mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-[#7c875a]" />
            Dokumentation &amp; Certificering
          </div>
          <h1 className="font-serif text-[32px] sm:text-5xl lg:text-6xl font-bold text-[#1b3a4b] tracking-tight">
            Dokumenter &amp; Uddannelse
          </h1>
          <p className="mt-4 text-lg sm:text-xl font-serif text-[#1b3a4b]/90 max-w-2xl mx-auto leading-relaxed">
            RAB-godkendelse, certifikater og faglige medlemskaber
          </p>
        </div>
      </section>

      {/* Main Content with Aside Contact Form */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            <div className="lg:col-span-7 xl:col-span-8 space-y-12">
          {/* Dansk Behandlerforbund Medlemsbevis */}
          <div className="bg-white rounded-3xl p-8 sm:p-10 shadow-sm border border-[#E5E0D8] space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-[#E5E0D8]">
              <div>
                <span className="text-xs font-semibold text-[#7c875a] uppercase tracking-wider block">
                  Brancheforening
                </span>
                <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1b3a4b]">
                  Dansk Behandlerforbund
                </h2>
              </div>
              <img
                src="/_assets/rab-logo-til-hjemmeside-og-mails.jpg"
                alt="RAB Logo"
                className="h-12 w-auto object-contain"
              />
            </div>

            <div className="p-5 rounded-2xl bg-[#FAF8F5] border border-[#E5E0D8] text-sm text-[#475569] space-y-2">
              <p className="font-semibold text-[#1b3a4b]">Dette bevis er udstedt af Dansk Behandlerforbund til:</p>
              <p className="text-lg font-serif font-bold text-[#1b3a4b]">Bo Steendahl</p>
              <p className="font-medium text-[#7c875a]">RAB Totum Kropsterapeut, A-medlemskab</p>
            </div>

            <div className="space-y-4 text-sm sm:text-base text-[#475569] leading-relaxed">
              <p>
                Dansk Behandlerforbund er en godkendt tværfaglig brancheforening for behandlere og skoler indenfor sundhedspleje under skat, sundhedsstyrelsen og styrelsen for patientsikkerhed.
              </p>
              <p>
                Dansk Behandlerforbund er en innovativ platform for uddannelse, efteruddannelse, etik, vidensdeling og netværk, der er essentiel for en kvalitetsorienteret og ansvarsfuld professionel behandler indenfor sundhedspleje. Dansk Behandlerforbund er klientens tryghedsgaranti for en etisk behandlingsstandard og synlig uddannelsesstandard.
              </p>
            </div>

            <div className="pt-2 flex flex-wrap gap-4">
              <a
                href="https://danskbehandlerforbund.dk/soeg-behandler/682/vis-behandler/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-xs font-semibold text-[#1b3a4b] hover:text-[#7c875a] underline"
              >
                <span>Verifikation af medlemskab på Dansk Behandlerforbund</span>
                <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true" />
              </a>
            </div>
          </div>

          {/* RAB-registreringsbevis #555 & 10 Forpligtelser */}
          <div className="bg-white rounded-3xl p-8 sm:p-10 shadow-sm border border-[#E5E0D8] space-y-6">
            <h3 className="font-serif text-2xl sm:text-3xl font-bold text-[#1b3a4b]">
              RAB-registreringsbevis
            </h3>
            <p className="text-sm font-medium text-[#64748B]">
              - for registrerede Totum kropsterapeuter under Styrelsen for Patientsikkerhed
            </p>
            <p className="text-sm text-[#475569]">
              Bevisets berettigelse verificeres på <a href="http://danskbehandlerforbund.dk" target="_blank" rel="noopener noreferrer" className="underline font-semibold text-[#1b3a4b]">danskbehandlerforbund.dk</a>.<br />
              Dette bevis er udstedt af Dansk Behandlerforbund til: <strong>Bo Steendahl #555</strong>
            </p>

            <div className="mt-4 pt-4 border-t border-[#E5E0D8]">
              <h4 className="font-serif font-bold text-lg text-[#1b3a4b] mb-4">
                VED MEDLEMSKAB FORPLIGTES FØLGENDE:
              </h4>
              <ul className="space-y-3 text-sm text-[#475569] leading-relaxed">
                {obligations.map((item, idx) => (
                  <li key={idx} className="p-3 rounded-lg bg-[#FAF8F5] border border-[#E5E0D8]/60">
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Hypnotherapy Certificate */}
          <div className="bg-white rounded-3xl p-8 sm:p-10 shadow-sm border border-[#E5E0D8] space-y-6">
            <span className="text-xs font-semibold text-[#7c875a] uppercase tracking-wider block">
              Hypnoterapi Uddannelse
            </span>
            <h3 className="font-serif text-2xl sm:text-3xl font-bold text-[#1b3a4b]">
              Certified Practitioner of Hypnotherapy
            </h3>
            <div className="rounded-2xl overflow-hidden border border-[#E5E0D8] shadow-md max-w-xl mx-auto">
              <img
                src="/_assets/1711543962553-15f9fabe-7704-4ead-8e4a-b185986203c1-1.jpg"
                alt="Certified Practitioner of Hypnotherapy"
                className="w-full h-auto object-contain"
              />
            </div>
              <div className="text-center pt-2">
                <a
                  href="https://totum.dk/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-xs font-semibold text-[#1b3a4b] hover:text-[#7c875a] underline"
                >
                  <span>Verifikation af Uddannelse - link til Totums hjemmeside</span>
                  <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true" />
                </a>
              </div>
            </div>
          </div>

          {/* Aside Contact Form */}
            <aside className="lg:col-span-5 xl:col-span-4 lg:sticky lg:top-24 space-y-6">
              <ContactForm />
              <div className="bg-white rounded-2xl p-5 border border-[#E5E0D8] text-xs text-[#475569] space-y-2.5 shadow-sm">
                <div className="font-semibold text-[#1b3a4b] text-sm">Direkte kontakt</div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-location-dot text-[#7c875a]" aria-hidden="true" />
                  <span>Vendersgade 37, 7000 Fredericia</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-phone text-[#7c875a]" aria-hidden="true" />
                  <a href="tel:53128213" className="hover:text-[#1b3a4b] font-medium">Tlf: 53 12 82 13</a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  )
}
